/**
 * Export Service for Admin Panel
 * Professional A4-optimized Excel (CSV) and PDF export
 */

interface ExportData {
  orders: any[];
  menuItems: any[];
  revenueData: any;
  period: string;
}

const LOGO_URL = '/saheeh_logo_w.svg';

class ExportService {
  async exportToExcel(data: ExportData): Promise<void> {
    try {
      const csvContent = this.generateCSV(data);
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `Saheeh_Report_${data.period.replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Excel export failed:', error);
      throw new Error('Failed to export to Excel');
    }
  }

  async exportToPDF(data: ExportData): Promise<void> {
    try {
      const htmlContent = this.generatePDFHTML(data);
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
        // Wait for logo image to load before triggering print
        const img = printWindow.document.querySelector('img');
        if (img) {
          img.onload = () => setTimeout(() => printWindow.print(), 300);
          img.onerror = () => setTimeout(() => printWindow.print(), 300);
        } else {
          setTimeout(() => printWindow.print(), 500);
        }
      }
    } catch (error) {
      console.error('PDF export failed:', error);
      throw new Error('Failed to export to PDF');
    }
  }

  private generateCSV(data: ExportData): string {
    const lines: string[] = [];

    lines.push('SAHEEH SUSHI & BOWLS');
    lines.push(`Report Period: ${data.period}`);
    lines.push(`Generated: ${new Date().toLocaleString()}`);
    lines.push('');

    lines.push('REVENUE SUMMARY');
    lines.push('Metric,Value');
    lines.push(`Total Revenue,"€${data.revenueData.total.toFixed(2)}"`);
    lines.push(`Total Orders,${data.revenueData.orders}`);
    lines.push(`Average Order Value,"€${data.revenueData.avgOrder.toFixed(2)}"`);
    lines.push('');

    // Category breakdown
    const categoryMap = new Map<string, { qty: number; revenue: number }>();
    data.orders.forEach(order => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach((item: any) => {
          const cat = item.category || item.category_name || 'Other';
          const price = parseFloat(item.price || item.price_with_tax || 0);
          const qty = parseFloat(item.quantity || 1);
          const cur = categoryMap.get(cat) || { qty: 0, revenue: 0 };
          cur.qty += qty;
          cur.revenue += price * qty;
          categoryMap.set(cat, cur);
        });
      }
    });

    if (categoryMap.size > 0) {
      lines.push('SALES BY CATEGORY');
      lines.push('Category,Items Sold,Revenue');
      Array.from(categoryMap.entries())
        .sort((a, b) => b[1].revenue - a[1].revenue)
        .forEach(([cat, d]) => {
          lines.push(`"${cat}",${d.qty},"€${d.revenue.toFixed(2)}"`);
        });
      lines.push('');
    }

    lines.push('ORDERS DETAIL');
    lines.push('Order #,Customer,Items,Total,Status,Payment,Type,Date');
    data.orders.forEach(order => {
      const date = order.created_at instanceof Date ? order.created_at : new Date(order.created_at);
      lines.push([
        `"${order.order_number || order.id || ''}"`,
        `"${order.customer_name || 'Guest'}"`,
        order.items?.length || 0,
        `"€${parseFloat(order.total || 0).toFixed(2)}"`,
        `"${order.status || 'Unknown'}"`,
        `"${order.payment_method || order.paymentMethod || 'Cash'}"`,
        `"${order.order_type || order.orderType || 'Pickup'}"`,
        `"${date.toLocaleString()}"`,
      ].join(','));
    });
    lines.push('');

    lines.push('MENU ITEMS');
    lines.push('Item Name,Category,Price,Available');
    data.menuItems
      .sort((a: any, b: any) => (a.category || '').localeCompare(b.category || ''))
      .forEach((item: any) => {
        lines.push([
          `"${item.name}"`,
          `"${item.category || 'Uncategorized'}"`,
          `"€${parseFloat(item.price || 0).toFixed(2)}"`,
          item.available !== false ? 'Yes' : 'No',
        ].join(','));
      });

    return lines.join('\n');
  }

  private generatePDFHTML(data: ExportData): string {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

    // Build category data
    const categoryMap = new Map<string, { qty: number; revenue: number }>();
    data.orders.forEach(order => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach((item: any) => {
          const cat = item.category || item.category_name || 'Other';
          const price = parseFloat(item.price || item.price_with_tax || 0);
          const qty = parseFloat(item.quantity || 1);
          const cur = categoryMap.get(cat) || { qty: 0, revenue: 0 };
          cur.qty += qty;
          cur.revenue += price * qty;
          categoryMap.set(cat, cur);
        });
      }
    });
    const categories = Array.from(categoryMap.entries()).sort((a, b) => b[1].revenue - a[1].revenue);

    // Build top items
    const itemMap = new Map<string, { qty: number; revenue: number }>();
    data.orders.forEach(order => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach((item: any) => {
          const name = item.name || item.item_name || 'Unknown';
          const price = parseFloat(item.price || item.price_with_tax || 0);
          const qty = parseFloat(item.quantity || 1);
          const cur = itemMap.get(name) || { qty: 0, revenue: 0 };
          cur.qty += qty;
          cur.revenue += price * qty;
          itemMap.set(name, cur);
        });
      }
    });
    const topItems = Array.from(itemMap.entries()).sort((a, b) => b[1].revenue - a[1].revenue).slice(0, 10);

    // Payment methods
    const payMap = new Map<string, { count: number; total: number }>();
    data.orders.forEach(order => {
      const pm = (order.payment_method || order.paymentMethod || 'Cash').toLowerCase();
      const label = pm.includes('card') ? 'Card' : pm === 'cash' ? 'Cash' : pm.charAt(0).toUpperCase() + pm.slice(1);
      const cur = payMap.get(label) || { count: 0, total: 0 };
      cur.count += 1;
      cur.total += parseFloat(order.total || 0);
      payMap.set(label, cur);
    });
    const payments = Array.from(payMap.entries()).sort((a, b) => b[1].total - a[1].total);

    // Order status
    const statusMap = new Map<string, number>();
    data.orders.forEach(order => {
      const s = (order.status || 'pending').toLowerCase();
      statusMap.set(s, (statusMap.get(s) || 0) + 1);
    });
    const statuses = Array.from(statusMap.entries()).sort((a, b) => b[1] - a[1]);

    return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Saheeh Report - ${data.period}</title>
<style>
  @page {
    size: A4;
    margin: 15mm 18mm 20mm 18mm;
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    font-size: 10px;
    color: #1a1a1a;
    line-height: 1.5;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* Header */
  .report-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 12px;
    border-bottom: 2px solid #1a1a1a;
    margin-bottom: 16px;
  }
  .report-header img {
    height: 48px;
    width: auto;
  }
  .report-header .meta {
    text-align: right;
  }
  .report-header .meta .title {
    font-size: 14px;
    font-weight: 700;
    color: #1a1a1a;
    letter-spacing: -0.02em;
  }
  .report-header .meta .sub {
    font-size: 9px;
    color: #888;
    margin-top: 2px;
  }

  /* KPI row */
  .kpi-row {
    display: flex;
    gap: 12px;
    margin-bottom: 18px;
  }
  .kpi-card {
    flex: 1;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 10px 14px;
    text-align: center;
  }
  .kpi-card .value {
    font-size: 20px;
    font-weight: 700;
    color: #1a1a1a;
    letter-spacing: -0.02em;
  }
  .kpi-card .label {
    font-size: 8px;
    color: #888;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.04em;
    margin-top: 2px;
  }

  /* Section */
  .section {
    margin-bottom: 16px;
    page-break-inside: avoid;
  }
  .section-title {
    font-size: 11px;
    font-weight: 700;
    color: #1a1a1a;
    padding-bottom: 6px;
    border-bottom: 1px solid #e0e0e0;
    margin-bottom: 8px;
    letter-spacing: -0.01em;
  }

  /* Grid */
  .grid-2 {
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
  }
  .grid-2 > * { flex: 1; }

  /* Tables */
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 9px;
  }
  thead th {
    background-color: #f5f5f5;
    font-weight: 700;
    font-size: 8px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #666;
    padding: 6px 8px;
    text-align: left;
    border-bottom: 1px solid #ddd;
  }
  thead th.right { text-align: right; }
  tbody td {
    padding: 5px 8px;
    border-bottom: 1px solid #f0f0f0;
    color: #333;
  }
  tbody td.right { text-align: right; }
  tbody td.bold { font-weight: 600; }
  tbody td.green { color: #2E7D32; font-weight: 600; }
  tbody td.mono { font-family: 'SF Mono', 'Fira Code', monospace; }
  tbody tr:last-child td { border-bottom: none; }

  /* Status chips */
  .chip {
    display: inline-block;
    padding: 1px 8px;
    border-radius: 10px;
    font-size: 8px;
    font-weight: 600;
    text-transform: capitalize;
  }
  .chip-completed, .chip-delivered { background: #e8f5e9; color: #2E7D32; }
  .chip-preparing, .chip-confirmed { background: #e3f2fd; color: #1565C0; }
  .chip-pending { background: #f5f5f5; color: #666; }
  .chip-cancelled { background: #ffebee; color: #c62828; }
  .chip-ready { background: #fff3e0; color: #E65100; }

  /* Footer */
  .report-footer {
    margin-top: 20px;
    padding-top: 10px;
    border-top: 1px solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    font-size: 8px;
    color: #999;
  }

  /* Print overrides */
  @media print {
    body { margin: 0; }
    .no-print { display: none; }
    .section { page-break-inside: avoid; }
  }
</style>
</head>
<body>

  <!-- Header -->
  <div class="report-header">
    <img src="${LOGO_URL}" alt="Saheeh" onerror="this.style.display='none'">
    <div class="meta">
      <div class="title">Business Report &mdash; ${data.period}</div>
      <div class="sub">${dateStr} at ${timeStr}</div>
    </div>
  </div>

  <!-- KPIs -->
  <div class="kpi-row">
    <div class="kpi-card">
      <div class="value">&euro;${data.revenueData.total.toFixed(2)}</div>
      <div class="label">Total Revenue</div>
    </div>
    <div class="kpi-card">
      <div class="value">${data.revenueData.orders}</div>
      <div class="label">Total Orders</div>
    </div>
    <div class="kpi-card">
      <div class="value">&euro;${data.revenueData.avgOrder.toFixed(2)}</div>
      <div class="label">Avg Order Value</div>
    </div>
    <div class="kpi-card">
      <div class="value">${topItems.reduce((s, [, d]) => s + d.qty, 0)}</div>
      <div class="label">Items Sold</div>
    </div>
  </div>

  <!-- Sales by Category + Payment Methods -->
  <div class="grid-2">
    <div class="section">
      <div class="section-title">Sales by Category</div>
      <table>
        <thead><tr><th>Category</th><th class="right">Items</th><th class="right">Revenue</th></tr></thead>
        <tbody>
          ${categories.length > 0 ? categories.map(([cat, d]) => `
            <tr><td class="bold">${cat}</td><td class="right">${d.qty}</td><td class="right green">&euro;${d.revenue.toFixed(2)}</td></tr>
          `).join('') : '<tr><td colspan="3" style="text-align:center;color:#999;padding:12px;">No category data</td></tr>'}
          ${categories.length > 0 ? `<tr style="border-top:1px solid #ddd"><td class="bold">Total</td><td class="right bold">${categories.reduce((s, [, d]) => s + d.qty, 0)}</td><td class="right green">&euro;${categories.reduce((s, [, d]) => s + d.revenue, 0).toFixed(2)}</td></tr>` : ''}
        </tbody>
      </table>
    </div>
    <div class="section">
      <div class="section-title">Payment & Status</div>
      <table>
        <thead><tr><th>Payment Method</th><th class="right">Orders</th><th class="right">Revenue</th></tr></thead>
        <tbody>
          ${payments.map(([m, d]) => `<tr><td class="bold">${m}</td><td class="right">${d.count}</td><td class="right green">&euro;${d.total.toFixed(2)}</td></tr>`).join('') || '<tr><td colspan="3" style="text-align:center;color:#999;padding:12px;">No data</td></tr>'}
        </tbody>
      </table>
      <div style="margin-top:10px">
        <table>
          <thead><tr><th>Order Status</th><th class="right">Count</th></tr></thead>
          <tbody>
            ${statuses.map(([s, c]) => `<tr><td><span class="chip chip-${s}">${s}</span></td><td class="right">${c}</td></tr>`).join('') || '<tr><td colspan="2" style="text-align:center;color:#999;padding:12px;">No data</td></tr>'}
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <!-- Top Selling Items -->
  ${topItems.length > 0 ? `
  <div class="section">
    <div class="section-title">Top Selling Items</div>
    <table>
      <thead><tr><th>#</th><th>Item</th><th class="right">Qty Sold</th><th class="right">Revenue</th></tr></thead>
      <tbody>
        ${topItems.map(([name, d], i) => `<tr><td style="color:#999">${i + 1}</td><td class="bold">${name}</td><td class="right">${d.qty}</td><td class="right green">&euro;${d.revenue.toFixed(2)}</td></tr>`).join('')}
      </tbody>
    </table>
  </div>` : ''}

  <!-- Orders -->
  <div class="section">
    <div class="section-title">Orders (${data.orders.length})</div>
    ${data.orders.length > 0 ? `
    <table>
      <thead><tr><th>Order #</th><th>Customer</th><th>Items</th><th>Status</th><th>Payment</th><th class="right">Total</th><th class="right">Date</th></tr></thead>
      <tbody>
        ${data.orders.slice(0, 50).map(order => {
          const date = order.created_at instanceof Date ? order.created_at : new Date(order.created_at);
          const status = (order.status || 'pending').toLowerCase();
          return `<tr>
            <td class="mono bold">#${order.order_number || (order.id || '').slice(0, 8)}</td>
            <td>${order.customer_name || 'Guest'}</td>
            <td>${order.items?.length || 0}</td>
            <td><span class="chip chip-${status}">${status}</span></td>
            <td>${(order.payment_method || order.paymentMethod || 'Cash')}</td>
            <td class="right green">&euro;${parseFloat(order.total || 0).toFixed(2)}</td>
            <td class="right" style="white-space:nowrap">${date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</td>
          </tr>`;
        }).join('')}
      </tbody>
    </table>
    ${data.orders.length > 50 ? `<div style="text-align:center;color:#999;font-size:8px;margin-top:6px">Showing 50 of ${data.orders.length} orders. Export to Excel for full data.</div>` : ''}
    ` : '<div style="text-align:center;color:#999;padding:20px">No orders in this period</div>'}
  </div>

  <!-- Menu Items (limited) -->
  <div class="section">
    <div class="section-title">Menu Items (${data.menuItems.length})</div>
    <table>
      <thead><tr><th>Item</th><th>Category</th><th class="right">Price</th><th>Available</th></tr></thead>
      <tbody>
        ${data.menuItems
          .sort((a: any, b: any) => (a.category || '').localeCompare(b.category || ''))
          .slice(0, 30)
          .map((item: any) => `
            <tr>
              <td class="bold">${item.name}</td>
              <td>${item.category || 'Uncategorized'}</td>
              <td class="right">&euro;${parseFloat(item.price || 0).toFixed(2)}</td>
              <td>${item.available !== false ? '<span style="color:#2E7D32">Yes</span>' : '<span style="color:#c62828">No</span>'}</td>
            </tr>
          `).join('')}
      </tbody>
    </table>
    ${data.menuItems.length > 30 ? `<div style="text-align:center;color:#999;font-size:8px;margin-top:6px">Showing 30 of ${data.menuItems.length} items. Export to Excel for full data.</div>` : ''}
  </div>

  <!-- Footer -->
  <div class="report-footer">
    <div>SAHEEH SUSHI & BOWLS &mdash; Business Report</div>
    <div>Generated ${dateStr} at ${timeStr} &bull; Saheeh Admin Panel</div>
  </div>

</body>
</html>`;
  }
}

export default new ExportService();
